import { Button, Stack } from "@mui/material";
import React, { useCallback } from "react";
import Modal, { CommonModalProps } from "..";
import { GetFormIsValid } from "../../../Redux/Reducers/redux-slice-form-validation";
import { useAppSelector } from "../../../Redux/redux-store";
import FormInputDate from "../../Forms/FormInputDate/form-input-date";
import GlobalConst from "../../../Core/Utils/Constants/globals";
import { DbFileTransferInfos } from "../../../Core/Api/Types/db-file-transfer-infos";
import dayjs from "dayjs";
import FormAutocomplete from "../../Forms/FormAutocomplete/form-autocomplete";
import { IsEmail } from "../../../Utils/helpers";
import { fileService } from "../../../Core/Api/services";
import useCustomSnackbar from "../../../Utils/Hooks/useCustomSnackbar";
import { useTranslation } from "react-i18next";

export type ModalTransferEditElementProps = {
  transfer: DbFileTransferInfos;
  onConfirm?: () => void;
};

export type ModalTransferEditProps = CommonModalProps<ModalTransferEditElementProps>;

const ModalTransferEdit = React.forwardRef<any, ModalTransferEditProps>((props, ref) => {
  const { t } = useTranslation();
  const { closeModal, onConfirm, ...modalProps } = props;
  const { transfer: _transfer } = modalProps;
  const [date, setDate] = React.useState<string>(_transfer.expireDate!);
  const [emails, setEmails] = React.useState<string[]>([]);

  const maxDays = 30;
  const enqueueCustomSnackbar = useCustomSnackbar();

  const formId = `transfer-edit`;
  const formValid = useAppSelector(GetFormIsValid(formId));

  const handleConfirmClick = useCallback(() => {
    fileService
      .UpdateTransfer({ transferId: _transfer.id, expireDate: date, recipientEmails: emails })
      .then((response) => {
        if (response.success) {
          enqueueCustomSnackbar(t("alert:success.transferUpdated"), "success");
          onConfirm && onConfirm();
          closeModal();
        } else enqueueCustomSnackbar(t("alert:error.oopsWithMessage", { message: response.message }), "error");
      });
  }, [date, emails, _transfer]);

  const handleCancelClick = () => {
    closeModal();
  };

  return (
    <Modal
      title={t("modal:title.updateTransfer")}
      reference={ref}
      closeModal={handleCancelClick}
      width={504}
      footer={
        <Stack direction="row" justifyContent="space-between">
          <Button onClick={handleCancelClick}>{t("common:cancel")}</Button>
          <Button color="success" variant="outlined" onClick={handleConfirmClick} disabled={!formValid}>
            {t("common:confirm")}
          </Button>
        </Stack>
      }
    >
      <Stack spacing={3}>
        <FormInputDate
          label={t("field:label.expirationDate")}
          format={GlobalConst.MOMENT_DATE_FORMAT_DATENOHOURS}
          hideReset
          validationProps={{
            formId: formId,
            formSectionId: "general",
            fieldId: "date",
            required: true,
            initialValue: new Date(date),
          }}
          value={dayjs(date)}
          onChange={(value) => {
            setDate(value.toJSON());
          }}
          maxDate={dayjs(_transfer.createdAt!).add(maxDays, "days")}
        />

        <FormAutocomplete
          label={t("field:label.newRecipientEmails")}
          placeholder={t("field:placeholder.addEmail")}
          options={[]}
          value={emails}
          multiple
          freeSolo
          onChange={(v) => {
            if (v.length > 0 && !IsEmail(v[v.length - 1])) return;

            setEmails(v ?? []);
          }}
          getOptionLabel={(value: any) => value}
          isOptionEqualToValue={(option, value) => option === value}
          filterOptions={(x, s) => x.filter((a) => a?.toLowerCase().includes(s.inputValue.toLowerCase()))}
          validationProps={{
            formId: formId,
            formSectionId: "general",
            fieldId: "emails",
            required: false,
            initialState: false,
          }}
        />
      </Stack>
    </Modal>
  );
});

export default ModalTransferEdit;
